'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import SiteNav from '@/components/SiteNav';
import LandingFooter from '@/app/landing-page/components/LandingFooter';
import { ArrowRight, Copy, Check } from 'lucide-react';

function CopyPromptBox({ children }: { children: React.ReactNode }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const plainText = typeof children === 'string' ? children : '';
    navigator.clipboard.writeText(plainText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className="relative rounded-xl p-6 my-6"
      style={{ backgroundColor: 'rgba(212,137,26,0.05)', border: '1px solid rgba(212,137,26,0.25)', borderLeft: '4px solid #D4891A' }}
    >
      <div className="font-body text-sm pr-10" style={{ color: '#2d3748', lineHeight: '1.85' }}>
        {children}
      </div>
      <button
        onClick={handleCopy}
        className="absolute top-4 right-4 flex items-center gap-1.5 font-body text-xs font-semibold px-3 py-1.5 rounded-lg transition-all duration-150"
        style={{ backgroundColor: copied ? 'rgba(212,137,26,0.15)' : 'rgba(212,137,26,0.1)', color: '#D4891A' }}
        aria-label="Copy prompt"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

export default function AiForCareerReinventionPage() {
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "AI For Career Reinvention After 50",
    "url": "https://thatclicked.com/learn/ai-for-career-reinvention",
    "author": {
      "@type": "Person",
      "name": "Anna Rippon",
      "url": "https://thatclicked.com/about"
    },
    "publisher": {
      "@type": "Organization",
      "name": "That Clicked",
      "url": "https://thatclicked.com"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://thatclicked.com/learn/ai-for-career-reinvention"
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9F7F4' }}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <SiteNav currentPath="/learn/ai-for-career-reinvention" />

      <main className="pt-24 pb-24 lg:pt-36 lg:pb-36">
        <div className="max-w-2xl mx-auto px-6 lg:px-0">

          <div className="flex items-center gap-2 mb-6 mt-8">
            <div className="h-px w-8 flex-shrink-0" style={{ backgroundColor: '#D4891A' }} aria-hidden="true" />
            <span className="font-body text-xs font-semibold uppercase tracking-widest" style={{ color: '#D4891A', letterSpacing: '0.12em' }}>
              By Anna Rippon, ThatClicked
            </span>
          </div>

          <h1
            className="font-display font-semibold mb-8"
            style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', color: '#1B2A4A', lineHeight: '1.18', letterSpacing: '-0.01em' }}
          >
            AI For Career Reinvention After 50
          </h1>

          <div className="h-px w-full mb-10" style={{ backgroundColor: 'rgba(27,42,74,0.12)' }} aria-hidden="true" />

          <div className="space-y-6 font-body" style={{ color: '#2d3748', lineHeight: '1.85', fontSize: '1.05rem' }}>

            <h2 className="font-display font-semibold pt-2" style={{ fontSize: '0.85rem', color: '#D4891A', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              THE SITUATION
            </h2>

            <p>
              You are good at what you do. You have spent twenty or thirty years getting good at it.
            </p>

            <p>
              And yet something has shifted. Maybe the role has changed around you. Maybe the organisation has. Maybe you have simply started to wonder whether this is still what you want to be doing for the next ten or fifteen years.
            </p>

            <p>
              The question of what comes next — consulting, something portfolio-based, building something of your own, a genuine change of direction — is one that most experienced professionals find genuinely difficult to think through clearly.
            </p>

            <p>
              Not because they lack capability. Because the question is large, the options feel unclear, and the people around them are not always the right sounding board.
            </p>

            <p>
              AI is surprisingly useful here. Not as a career counsellor. As a thinking partner.
            </p>

            <h2 className="font-display font-semibold pt-4" style={{ fontSize: '1.25rem', color: '#1B2A4A' }}>
              What AI can do for career reinvention
            </h2>

            <h3 className="font-display font-semibold pt-2" style={{ fontSize: '1.1rem', color: '#1B2A4A' }}>
              Help you think clearly about what you have
            </h3>

            <p>
              Before you can work out what comes next, it helps to see clearly what you are bringing with you. AI can help you articulate your experience in ways that are difficult to do alone — not because you do not know it, but because you are too close to it. Ask it to help you identify your most transferable skills, your most valuable experience, and the things you have done that other people would pay for.
            </p>

            <p>
              The output is a starting point, not a verdict. But it often surfaces things people have undervalued about themselves.
            </p>

            <h3 className="font-display font-semibold pt-2" style={{ fontSize: '1.1rem', color: '#1B2A4A' }}>
              Test ideas before you commit to them
            </h3>

            <p>
              If you have an idea — a consultancy, a course, a service, a change of sector — AI can help you stress-test it before you spend significant time or money on it.
            </p>

            <p>
              Ask it to tell you honestly whether the idea is viable, what the risks are, and what the simplest version of it you could test would look like. Push back on the answer. Ask it to find the weaknesses in your thinking.
            </p>

            <p>
              This is not a replacement for talking to people who have done it. But it is a useful way to think something through before those conversations.
            </p>

            <h3 className="font-display font-semibold pt-2" style={{ fontSize: '1.1rem', color: '#1B2A4A' }}>
              Write your professional bio for where you are going
            </h3>

            <p>
              Most professional bios describe where someone has been. The bios that open doors describe where someone is going and why the right person should want to talk to them.
            </p>

            <p>
              AI can help you write this version. Give it your background and tell it what kind of work or opportunity you are moving toward. Ask it to write a bio that positions you for where you want to go, not just where you have been.
            </p>

            <p>
              Read what comes back. Adjust the parts that do not sound like you. Use the parts that do.
            </p>

            <h3 className="font-display font-semibold pt-2" style={{ fontSize: '1.1rem', color: '#1B2A4A' }}>
              Prepare for difficult conversations about change
            </h3>

            <p>
              Career reinvention often involves conversations that feel high-stakes — with a partner, with a current employer, with a potential new client. AI can help you prepare for these. Tell it the conversation you need to have, who it is with, and what you are worried about. Ask it to help you think through how to approach it.
            </p>

            <h2 className="font-display font-semibold pt-4" style={{ fontSize: '1.25rem', color: '#1B2A4A' }}>
              What AI cannot do for career reinvention
            </h2>

            <p>
              Make the decision for you. That part is yours.
            </p>

            <p>
              AI can help you think more clearly. It cannot tell you what you want, whether a risk is worth taking for you specifically, or whether a particular path will make you happier.
            </p>

            <p>
              It is also not a substitute for talking to people who have made similar transitions. Real conversations with real people who have done what you are considering are irreplaceable. AI is useful before and after those conversations — for preparing your thinking going in and processing what you heard coming out.
            </p>

            <h2 className="font-display font-semibold pt-4" style={{ fontSize: '1.25rem', color: '#1B2A4A' }}>
              A prompt worth trying
            </h2>

            <CopyPromptBox>
              I have [describe your background — industry, role, years of experience, key skills]. I am considering [describe what you are thinking about — consulting, a career change, building something of your own]. I want you to help me think through this seriously. Is this a real opportunity given what I have described? What is the simplest version I could test before committing? What are the two or three biggest risks I should be thinking about? And what would you tell me that I might not want to hear?
            </CopyPromptBox>

            <p>
              The last instruction matters. AI defaults to encouragement. Asking it for the thing you might not want to hear produces more useful output.
            </p>

            <h2 className="font-display font-semibold pt-4" style={{ fontSize: '1.25rem', color: '#1B2A4A' }}>
              Honest expectations
            </h2>

            <p>
              AI is a good thinking partner for career reinvention. It is patient, it does not get tired of the conversation, and it will not tell you what it thinks you want to hear if you ask it not to.
            </p>

            <p>
              What it is not: a replacement for your own judgment, the people who know you well, or professionals who specialise in this kind of transition.
            </p>

            <p>
              Use it as one voice in the conversation — a useful one — not the only one.
            </p>

          </div>

          {/* Where to go deeper */}
          <div className="mt-14 rounded-xl p-8 border" style={{ backgroundColor: 'white', borderColor: 'rgba(27,42,74,0.12)' }}>
            <h2 className="font-display font-semibold mb-5" style={{ fontSize: '1.15rem', color: '#1B2A4A' }}>
              Where to go deeper
            </h2>
            <p className="font-body text-sm mb-5" style={{ color: '#2d3748', lineHeight: '1.75' }}>
              The Deep Dive Vault — available to ThatClicked members who complete all nine modules — contains dedicated lessons on building something with AI, pricing what you are worth, and preparing for the high-stakes career conversations that come with reinvention. Module 8 (Career Protection) and Module 9 (Your Next Chapter) also cover this territory directly.
            </p>
            <ul className="space-y-2">
              <li>
                <Link href="/free-lesson-screen" className="font-body text-sm" style={{ color: '#1B2A4A' }}>
                  → Module 8: Career Protection <span style={{ color: '#6b7280' }}>(paid)</span>
                </Link>
              </li>
              <li>
                <Link href="/free-lesson-screen" className="font-body text-sm" style={{ color: '#1B2A4A' }}>
                  → Module 9: Your Next Chapter <span style={{ color: '#6b7280' }}>(paid)</span>
                </Link>
              </li>
              <li>
                <Link href="/free-lesson-screen" className="font-body text-sm" style={{ color: '#1B2A4A' }}>
                  → Deep Dive Vault: Building Something With AI <span style={{ color: '#6b7280' }}>(paid)</span>
                </Link>
              </li>
            </ul>
          </div>

          <div className="h-px w-full mt-14 mb-12" style={{ backgroundColor: 'rgba(27,42,74,0.12)' }} aria-hidden="true" />

          <div className="text-center">
            <p className="font-body text-base mb-6" style={{ color: 'rgba(27,42,74,0.7)' }}>That Clicked is a plain-English AI training platform for professionals over 50.</p>
            <Link
              href="/free-lesson-screen"
              className="group inline-flex items-center justify-center gap-2.5 font-body font-semibold text-base px-8 py-4 rounded-xl transition-all duration-200 active:scale-95"
              style={{ backgroundColor: '#D4891A', color: 'white' }}
            >
              Learn more AI skills — first two lessons free
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>

        </div>
      </main>

      <LandingFooter />
    </div>
  );
}
